<template>
  <div class="container-fluid py-4" v-if="isStandalone">
    <div class="mb-3">
      <router-link to="/golf" class="text-muted text-decoration-none small">
        <i class="material-icons align-middle" style="font-size:16px;">arrow_back</i>
        {{ $t('golf.backToDashboard') }}
      </router-link>
    </div>
    <green-detail-content
      :hole="hole"
      :clippings="clippings"
      :stimp-readings="stimpReadings"
      :fertilizers="fertilizers"
      :fertilizer-products="fertilizerProducts"
      :stats="stats"
      :selected-period="selectedPeriod"
      :is-admin="isAdmin"
      @record-clipping="recordClipping"
      @record-stimp="recordStimp"
      @record-fertilizer="recordFertilizer"
      @update-clipping="updateClipping"
      @delete-clipping="deleteClipping"
      @update-stimp="updateStimp"
      @delete-stimp="deleteStimp"
    />
  </div>
  <div v-else>
    <div class="mb-4">
      <a href="#" class="text-muted text-decoration-none small" @click.prevent="$emit('close')">
        <i class="material-icons align-middle" style="font-size:16px;">arrow_back</i>
        {{ $t('golf.backToDashboard') }}
      </a>
    </div>
    <green-detail-content
      :hole="hole"
      :clippings="clippings"
      :stimp-readings="stimpReadings"
      :fertilizers="fertilizers"
      :fertilizer-products="fertilizerProducts"
      :stats="stats"
      :selected-period="selectedPeriod"
      :is-admin="isAdmin"
      @record-clipping="recordClipping"
      @record-stimp="recordStimp"
      @record-fertilizer="recordFertilizer"
      @update-clipping="updateClipping"
      @delete-clipping="deleteClipping"
      @update-stimp="updateStimp"
      @delete-stimp="deleteStimp"
    />
  </div>
</template>

<script>
import { golfApi as api } from "../../services/api";
import GreenDetailContent from "./GreenDetailContent.vue";
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

export default {
  components: { GreenDetailContent },
  props: {
    holeId: { type: Number, required: true },
  },
  emits: ['close'],
  data() {
    return {
      hole: {},
      clippings: [],
      stimpReadings: [],
      fertilizers: [],
      fertilizerProducts: [],
      stats: {},
      selectedPeriod: 28,
    };
  },
  computed: {
    isStandalone() {
      return this.$route && this.$route.name === 'Golf Hole Detail';
    },
    isAdmin() {
      try {
        const token = Cookies.get('accessToken');
        if (!token) return false;
        const role = jwtDecode(token).role;
        return role === 'Admin' || role === 'SuperAdmin';
      } catch { return false; }
    },
  },
  mounted() {
    this.fetchDetail();
    this.fetchFertilizerProducts();
  },
  methods: {
    async fetchDetail() {
      try {
        const res = await api.get(`/holes/${this.holeId}?days=${this.selectedPeriod}`);
        const data = res.data;
        this.hole = {
          holeNumber: data.holeNumber,
          name: data.name,
          par: data.par,
          handicapIndex: data.handicapIndex,
          greenInfo: data.greenInfo || null,
          fairwayInfo: data.fairwayInfo || null,
          tees: data.tees || [],
        };
        this.clippings = data.clippings || [];
        this.stimpReadings = data.stimpReadings || [];
        this.fertilizers = data.fertilizerApplications || [];
        this.stats = {
          clippingsAvg: data.avgClippingLiters ?? null,
          clippingsStdDev: data.stdDevClippingLiters ?? null,
          stimpAvg: data.avgStimpSpeed ?? null,
          stimpStdDev: data.stdDevStimpSpeed ?? null,
        };
      } catch (error) {
        console.error('Failed to load hole detail:', error); // eslint-disable-line no-console
      }
    },
    async fetchFertilizerProducts() {
      try {
        const res = await api.get('/fertilizer-products');
        this.fertilizerProducts = (res.data || []).filter(p => p.isActive);
      } catch (error) {
        console.error('Failed to load fertilizer products:', error); // eslint-disable-line no-console
      }
    },
    async recordClipping({ value, date }) {
      if (!value) return;
      try {
        await api.post('/clippings', { golfGreenId: this.hole.greenInfo.id, liters: value, recordedAt: date || undefined });
        this.$toast.success(this.$t('common.created'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to record clipping:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async recordStimp({ value, date }) {
      if (!value) return;
      try {
        await api.post('/stimp', { golfGreenId: this.hole.greenInfo.id, speedFeet: value, recordedAt: date || undefined });
        this.$toast.success(this.$t('common.created'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to record stimp:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async updateClipping({ id, value, date }) {
      if (!id || !value) return;
      try {
        await api.put(`/clippings/${id}`, { liters: value, recordedAt: date || undefined });
        this.$toast.success(this.$t('common.updated'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to update clipping:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async deleteClipping({ id }) {
      if (!id || !confirm(this.$t('delete.areYouSure'))) return;
      try {
        await api.delete(`/clippings/${id}`);
        this.$toast.success(this.$t('common.deleted'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to delete clipping:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async updateStimp({ id, value, date }) {
      if (!id || !value) return;
      try {
        await api.put(`/stimp/${id}`, { speedFeet: value, recordedAt: date || undefined });
        this.$toast.success(this.$t('common.updated'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to update stimp:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async deleteStimp({ id }) {
      if (!id || !confirm(this.$t('delete.areYouSure'))) return;
      try {
        await api.delete(`/stimp/${id}`);
        this.$toast.success(this.$t('common.deleted'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to delete stimp:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    async recordFertilizer({ productName, amountKg, date }) {
      if (!productName || !amountKg) return;
      try {
        await api.post('/fertilizer', {
          golfGreenId: this.hole.greenInfo.id,
          productName,
          amountKg,
          appliedAt: date || undefined,
        });
        this.$toast.success(this.$t('common.created'));
        this.fetchDetail();
      } catch (error) {
        console.error('Failed to record fertilizer:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
  },
};
</script>
